/* eslint-disable @typescript-eslint/ban-ts-comment */
import { RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { StyledDivider, StTextContainer } from '../Subscribe.styles';
import { StDTPGridContainer } from '../../../components/shared/Container/Container.styles';
import { SubscribeProps } from '../Subscribe.types';
import { post } from '../../../lib/utils/axiosFetcher';
import { CDP_EVENTS } from '../../../../src/constants';
import * as cdpService from '../../../services/CdpService';
import { hideLoader, showLoader } from '../../../features/loaderSlice';
import { useDispatch } from 'react-redux';
import { setCookie } from 'cookies-next';
import {
  ENROLLMENT_EXPIRY_TIME_FALLBACK,
  getCookieExpiryTime,
} from '../../../lib/utils/enrollmentUtil';
import UiHeading from '../../shared/Heading';
import UiSubHeading from '../../shared/Typography/UiSubHeading';
import UiText from '../../shared/Typography/UiText';
import UiInput from '../../shared/UiInput';
import ButtonPrimary from '../../shared/Button/PrimaryButton';
import isClinicalTrialSite from '../../../lib/utils/siteHelper';
import FormToolTip from '../../../components/shared/FormElements/FormToolTip';
import { regExpFromSitecorePattern } from '../../../lib/utils/contactForm';

type CheckboxInputProps = ControllerRenderProps & CheckboxProps & any;

const Unsubscribe = (props: SubscribeProps): JSX.Element => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const { sitecoreContext } = useSitecoreContext();
  const siteName = sitecoreContext?.site?.name;
  const dispatch = useDispatch();

  const {
    control,
    trigger,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = useForm({ mode: 'all' });
  const {
    heading,
    content,
    content1,
    link,
    emailInputName,
    emailLabel,
    tooltipLabel,
    emailPlaceholder,
    isEmailRequired,
    isConsentRequired,
    requiredEmailValidationMessage,
    emailRegex,
    invalidInputEmailValidationMessage,
    serverSideEmailValidationError,
    consentCheckboxName,
    consentDescription,
    consentRequiredValidationMessage,
    dividerColor,
  } = props?.fields || {};
  const CheckboxInput: React.FC<CheckboxInputProps> = ({ value, ...rest }: CheckboxInputProps) => {
    return (
      <Checkbox
        checked={!!value}
        {...rest}
        sx={{
          alignSelf: 'flex-start',
          padding: '0 9px',
          '.MuiCheckbox-root': {
            color: 'black',
            backgroundColor: 'black',
          },
        }}
        className="checkbox"
        value="consent"
        color="primary"
      />
    );
  };
  useEffect(() => {
    if (router?.isReady) {
      dispatch(showLoader());
      const unsubscribeToken = router.query?.uToken as string;
      unsubscribePatient(unsubscribeToken);
      dispatch(hideLoader());
    }
  }, [router?.isReady]);

  const unsubscribePatient = async (unsubscribeToken: any) => {
    try {
      cdpService.event(CDP_EVENTS.UNSUBSCRIBE_CLICK, {});
      const ppId = await post('/Send/Unsubscribe', {
        unsubscribeToken: unsubscribeToken,
        siteSettingsId: sitecoreContext?.siteSettingsId,
        language: sitecoreContext?.language,
      });
      if (ppId !== undefined && ppId !== null && ppId !== '') {
        setCookie('bx_guest_ref', ppId?.data?.guestRef, {
          expires: getCookieExpiryTime(ENROLLMENT_EXPIRY_TIME_FALLBACK),
          secure: true,
        });
        cdpService.identityEvent(ppId?.data?.email);
        if (isClinicalTrialSite(siteName as string)) {
          cdpService.event(CDP_EVENTS.BASE_PROFILE_UPDATED, {
            reference: 'email_unsubscribe_update',
          });
        }
      }
    } catch {}
  };
  const handleResubscribe = async (e: any) => {
    dispatch(showLoader());
    e.preventDefault();
    const isFormValid = await trigger();
    if (isFormValid) {
      try {
        const email = getValues('email-input' as never);
        cdpService.event(CDP_EVENTS.RESUBSCRIBE_CLICK, {});
        const isValid = await post('/Send/Resubscribe', {
          unsubscribeToken: router?.query?.uToken as string,
          emailAddress: email,
          siteSettingsId: sitecoreContext?.siteSettingsId,
          language: sitecoreContext?.language,
        });
        if (isValid) {
          if (isClinicalTrialSite(siteName as string)) {
            cdpService.event(CDP_EVENTS.BASE_PROFILE_UPDATED, {
              reference: 'email_resubscribe_update',
            });
          }
          router.push(`${link?.value?.href}`);
        } else {
          setError('emailValidation', {
            type: 'server',
          });
        }
      } catch {
        setError('emailValidation', {
          type: 'server',
        });
      }
    }
    dispatch(hideLoader());
  };
  if (!props?.fields) return <></>;
  return (
    <StDTPGridContainer>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        rowGap="2rem"
        sx={{ marginBlock: '2rem' }}
      >
        <UiHeading size="h2" data-testid="title" color="primary" align="center">
          {heading}
        </UiHeading>
        <StyledDivider bgcolor={dividerColor?.value} />
        <StTextContainer>
          <UiSubHeading data-testid="content" align="center" color="default">
            <RichText field={content} />
          </UiSubHeading>
        </StTextContainer>
        <StTextContainer>
          <UiText data-testid="content1" align="center" color="default">
            <RichText field={content1} />
          </UiText>
        </StTextContainer>
        <Box
          sx={{
            width: isMobile ? '100%' : '360px',
            alignSelf: 'center',
          }}
        >
          {/* @ts-ignore */}
          <Box
            sx={{
              '.MuiInputBase-root': {
                color: `${theme.palette.common.black} !important`,
              },
            }}
          >
            <UiInput
              label={
                <>
                  <Text field={emailLabel} />
                  <FormToolTip value={tooltipLabel?.value as string} />
                </>
              }
              name={emailInputName?.value as string}
              placeholder={emailPlaceholder?.value as string}
              control={control}
              rules={{
                required: isEmailRequired?.value ? requiredEmailValidationMessage?.value : ``,
                validate: async (value: string) => {
                  clearErrors('emailValidation');
                  const regEx = regExpFromSitecorePattern(emailRegex?.value);
                  return regEx.test(value) ? true : invalidInputEmailValidationMessage?.value ?? '';
                },
              }}
            />
          </Box>
          <Box sx={{ color: theme.palette.error.main, fontSize: '0.8125rem' }}>
            {errors?.emailValidation &&
              (serverSideEmailValidationError?.value ??
                'You have entered an incorrect email address')}
          </Box>
        </Box>
        <Box sx={{ width: isMobile ? '100%' : '360px', alignSelf: 'center' }}>
          <Controller
            control={control}
            name={consentCheckboxName?.value as string}
            rules={{
              required: {
                value: isConsentRequired?.value || false,
                message: consentRequiredValidationMessage?.value || '',
              },
            }}
            render={({ field }) => (
              <FormControl
                sx={{
                  color: (theme: any) => theme.palette.common.black,
                }}
              >
                <FormControlLabel
                  className="checkboxLabel"
                  control={<CheckboxInput {...field} />}
                  label={<RichText className="description" field={consentDescription} />}
                />
              </FormControl>
            )}
          />
          {errors && errors[consentCheckboxName?.value as string] && (
            <Typography
              sx={{
                color: theme.palette.error.main,
                fontWeight: 400,
                fontSize: '0.8125rem',
                lineHeight: '1.25rem',
                marginLeft: '30px',
              }}
            >
              {`${errors[consentCheckboxName?.value as string]?.message}`}
            </Typography>
          )}
        </Box>
        <ButtonPrimary
          data-testid="link"
          onClick={(e: any) => {
            clearErrors('emailValidation');
            handleResubscribe(e);
          }}
        >
          {link?.value?.text}
        </ButtonPrimary>
      </Box>
    </StDTPGridContainer>
  );
};

export default Unsubscribe;
